import { useState, useEffect, useContext } from 'react';

import { useHelpBundle } from '@plasma/ui.application.app';
import { useNavigate } from '@plasma/ui.application.router';
import notification from '@plasma/ui.feedback.notification';
import { useResourceBundle, useTranslation } from '@plasma/ui.utils.i18n';

import { PlantLayoutContext } from '../../index';
import { fetchAllDataGet_byApiName, fetchAllDataGetFromAppFile_byApiName } from '../../services/gets/getdata';
import { sendAllFileByPost, getFileByGuid_Post, pollForFile } from '../../services/posts/postdata';
import translations from './i18n';
import { PlantLayoutAreaDrawingsProps } from './plant-layout-area-drawings';
import help from './plant-layout-area-drawings.help';
import { apiUriDesignDrawingsHttp, apiUriMasterDataHttp } from '../../services/conf/apiConf';

function usePlantLayoutAreaDrawingsHook(props: PlantLayoutAreaDrawingsProps) {
  useResourceBundle(translations, undefined, '4b1abfa6-ba80-4817-87c0-4bb5e2944270');
  useHelpBundle(help);

  const { t } = useTranslation();

  const [areaNameInModal, setAreaNameInModal] = useState<string | null>(null);
  const [zoneNameInModal, setZoneNameInModal] = useState<string | null>(null);
  const [selectedZoneDrawing, setSelectedZoneDrawing] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File>();
  const [buttons, setButtons] = useState<any>([]);
  const [distinctArea, setdistinctArea] = useState<any>([]);
  const [dataFromApi, setdataFromApi] = useState([]);
  const [zoneAndAreaCodesFromPlantLayoutApi, setzoneAndAreaCodesFromPlantLayoutApi] = useState<any[] | undefined>([]);
  const [dataForGuid, setdataForGuid] = useState([]);
  const [showPhoto, setshowPhoto] = useState(false);
  const [showModal, setshowModal] = useState(false);
  const [showAreaZoneDrawingSelectionModal, setshowAreaZoneDrawingSelectionModal] = useState(false);
  const [showButtonBox, setshowButtonBox] = useState(true);
  const [guidByProd, setguidByProd] = useState('');
  const [guidByProdArray, setguidByProdArray] = useState([]);
  const [iFrameSrc, setIframeSrc] = useState('');
  const [buttonTitle, setButtonTitle] = useState('');
  const [count, setcount] = useState(0);
  const [pageNumber, setpageNumber] = useState(0);
  const [roleID, setroleID] = useState<number | null>(null);
  const [loadingFile, setLoadingFile] = useState(false);

  const navigate = useNavigate();
  const plantLayoutFromMemory = useContext(PlantLayoutContext);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoadingFile(true); // Start loading state immediately
    try {
      // Execute all API calls,  Use Promise.all if you want them to run in parallel (faster)
      const [masterAreas, documentGuids] = await Promise.all([
        fetchAllDataGet_byApiName('GetMasterAreas', apiUriMasterDataHttp()),
        fetchAllDataGetFromAppFile_byApiName('GetPlantLayoutAreaDocumentGuids', apiUriDesignDrawingsHttp()),
      ]);

      // console.log('PlantLayoutFromMemory', plantLayoutFromMemory)
      const zoneAndAreaCodes = Array.isArray(plantLayoutFromMemory?.layoutData) ?
                                plantLayoutFromMemory.layoutData.map((item) => ({
                                  areacode: item.areacode,
                                  zonecode: item.zonecode,
                                })) : [];
      
      setzoneAndAreaCodesFromPlantLayoutApi(zoneAndAreaCodes);
      setdataFromApi(masterAreas);
      setdataForGuid(documentGuids);
    } catch (error) {
      console.error('Failed to Fetch Data:', error);
    } finally {
      setLoadingFile(false);
    }
  };

  useEffect(() => {
    if (!roleID) {
      const storedData = sessionStorage.getItem('roleid');
      if (storedData) {
        setroleID(JSON.parse(storedData));
      }
    }
  }, [roleID]);

  useEffect(() => {
    const distinctAreas = dataFromApi?.map((item) => {
      return item.areaname
        .toLowerCase()
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    });
    setdistinctArea(distinctAreas);
  }, [dataFromApi]);

  //console.log(distinctArea);

  useEffect(() => {
    const buttonField = distinctArea?.map((item: any) => {
      const oneNewItem = { title: item };
      return oneNewItem;
    });
    setButtons(buttonField);
  }, [distinctArea]);

  const handleNextPage = () => {
    //console.log('guid array: ',guidByProdArray);
    if (guidByProdArray?.length > 1 && count < guidByProdArray?.length - 1) {
      const guidindex = count + 1;
      //console.log('page no. : ',guidindex+1);
      setpageNumber(guidindex + 1);
      setcount(count + 1);
      setguidByProd(guidByProdArray[guidindex]);
    }
  };

  const handlePreviousPage = () => {
    if (count > 0) {
      const guidindex = count - 1;
      setpageNumber(guidindex + 1);
      setcount(count - 1);
      setguidByProd(guidByProdArray[guidindex]);
    }
  };

  const handleButtonClick = (clickedTitle: string) => {
    if (!clickedTitle) return;

    const hasZones = zoneAndAreaCodesFromPlantLayoutApi?.some(
      (mapping) => mapping.areacode.toLowerCase() === clickedTitle.toLowerCase() && mapping.zonecode,
    );

    if (hasZones) {
      // Zones exist -> Open Modal to let user refine selection
      setButtonTitle(clickedTitle);
      setshowPhoto(false);
      setshowAreaZoneDrawingSelectionModal(true);
      setSelectedZoneDrawing('');
    } else {
      // FLOW B: No zones -> Direct Display
      setButtonTitle(clickedTitle);
      const directMatch = dataForGuid.find((item) => item.docfor.toLowerCase() === clickedTitle.toLowerCase());
      if (directMatch) {
        setguidByProdArray(directMatch.guid);
        setguidByProd(directMatch.guid[0]);
        fetchAndSetIframeSource(directMatch.guid[0]);
        setshowPhoto(true);
      }
    }
  };

  const findSelectedGuid = () => {
    if (!selectedZoneDrawing || !dataForGuid) return null;
    // Exact match logic
    let match = dataForGuid.find((item) => item.docfor.toLowerCase() === selectedZoneDrawing.toLowerCase());
    // Fallback match logic
    if (!match) {
      match = dataForGuid.find((item) => item.docfor.toLowerCase().includes(selectedZoneDrawing.toLowerCase()));
    }

    return match ? match.guid : null;
  };

  const handleConfirmSelection = async () => {
    if (!selectedZoneDrawing) {
      alert(t('please-select-an-option'));
      return;
    }

    const targetGuids = findSelectedGuid();
    if (targetGuids && targetGuids.length > 0) {
      setguidByProdArray(targetGuids);
      setguidByProd(targetGuids[0]);
      await fetchAndSetIframeSource(targetGuids);

      // NOW display the iframe and close the modal
      setshowPhoto(true);
      setshowAreaZoneDrawingSelectionModal(false);
      setpageNumber(1);
      setcount(0);
    } else {
      notification.info('Drawing not found', 'No drawing found for this selection');
    }
  };

  async function fetchAndSetIframeSource(guidToFetch: string) {
    if (!guidToFetch) return;
    const queryParams = new URLSearchParams({ guid: guidToFetch });
    setLoadingFile(true);
    try {
      const blob = await pollForFile(`getPlantLayoutAreaDesign?${queryParams}`, {}, undefined, undefined, apiUriDesignDrawingsHttp());
      const blobUrl = URL.createObjectURL(blob);
      setIframeSrc(blobUrl);
    } catch (error) {
      console.log(error);
      notification.error('Error !', 'Unsuccessful Attempt', undefined, 'medium', 3);
    } finally {
      setLoadingFile(false);
    }
  }

  const handleGoHome = () => {
    navigate('/home-screen');
  };

  const handleShowModal = () => {
    setshowModal(true);
  };

  const handleSaveFromModal = async () => {
    const formData = new FormData();
    const areaName = zoneNameInModal ? `${areaNameInModal}_${zoneNameInModal}` : areaNameInModal;

    if (!selectedFile) return;

    formData.append('File', selectedFile);
    formData.append('AreaName', areaName ?? '');
    setLoadingFile(true);

    try {
      const response = await sendAllFileByPost('uploadPlantLayoutAreaDesign', formData, undefined, apiUriDesignDrawingsHttp());
      if (response) {
        const [masterAreas, documentGuids] = await Promise.all([
          fetchAllDataGet_byApiName('GetMasterAreas', apiUriMasterDataHttp()),
          fetchAllDataGetFromAppFile_byApiName('GetPlantLayoutAreaDocumentGuids', apiUriDesignDrawingsHttp()),
        ]);

        setdataFromApi(masterAreas);
        setdataForGuid(documentGuids);
        notification.success('Success !', 'File Saved Successfully', undefined, 'medium', 3);
      }
    } catch (error) {
      console.error('Error during file save or refresh workflow:', error);
      notification.error('Error !', 'Unsuccessful Attempt', undefined, 'medium', 3);
    } finally {
      setLoadingFile(false);
      HandleModalCancel();
    }
  };

  const HandleModalCancel = () => {
    setAreaNameInModal(null);
    setZoneNameInModal(null);
    setshowModal(false);
    setshowAreaZoneDrawingSelectionModal(false);
  };

  const handleAreaChangeInModal = (value: string) => {
    setAreaNameInModal(value);
  };

  const handleZoneChangeInModal = (value: string) => {
    setZoneNameInModal(value);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    const allowedTypes = ['application/pdf'];
    if (file && allowedTypes?.includes(file.type)) {
      setSelectedFile(file);
    } else {
      event.target.value = ''; // Clear the input field
      alert('Please Select A PDF File.'); // Show an error message
    }
  };

  const handleButtonBoxOpen = () => {
    setshowButtonBox(!showButtonBox);
  };

  const handleDeletePDF = () => {
    const queryParams = new URLSearchParams({
      guid: guidByProd,
    });

    getFileByGuid_Post(`deletePlantLayoutAreaDesign?${queryParams}`, {}, apiUriDesignDrawingsHttp())
      .then((response) => {
        //console.log(response);
        if (response) {
          fetchAllDataGet_byApiName('GetMasterAreas', apiUriMasterDataHttp())
            .then((data) => {
              setdataFromApi(data);
              notification.success('Removed !', 'File Deleted Permanently', undefined, 'medium', 3);
            })
            .catch((error) => {
              console.log('Failed to EditPlantLayout', error);
            });

          fetchAllDataGetFromAppFile_byApiName('GetPlantLayoutAreaDocumentGuids', apiUriDesignDrawingsHttp())
            .then((data) => {
              //set the response
              setdataForGuid(data);
            })
            .catch((error) => {
              console.log('Failed to Fetch Data', error);
            });

          setpageNumber(1);
          setIframeSrc('');
          setSelectedZoneDrawing('');
          setshowPhoto(false);
        }
      })
      .catch((error) => {
        console.log(error);
        notification.error('Error !', 'Unsuccessful Attempt', undefined, 'medium', 3);
      });
  };

  return {
    t,
    showPhoto,
    loadingFile,
    buttons,
    showModal,
    showAreaZoneDrawingSelectionModal,
    setshowAreaZoneDrawingSelectionModal,
    zoneAndAreaCodesFromPlantLayoutApi,
    areaNameInModal,
    zoneNameInModal,
    handleZoneChangeInModal,
    distinctArea,
    showButtonBox,
    iFrameSrc,
    guidByProd,
    guidByProdArray,
    handleConfirmSelection,
    pageNumber,
    roleID,
    buttonTitle,
    selectedZoneDrawing,
    setSelectedZoneDrawing,
    handleGoHome,
    handleButtonClick,
    handleShowModal,
    handleSaveFromModal,
    HandleModalCancel,
    handleAreaChangeInModal,
    handleFileUpload,
    handleButtonBoxOpen,
    handleNextPage,
    handlePreviousPage,
    setButtonTitle,
    handleDeletePDF,
    setshowPhoto,
    setpageNumber,
  };
}

export default usePlantLayoutAreaDrawingsHook;
